import { EventFormOccurrenceUtil } from "../../modules/s25-event-creation-form/occurrences/event.form.occurrence.util";
import type { Layout } from "../../modules/s25-object-search/s25.object.search.util";
import type { AvailCheck } from "../../pojo/AvailabilityCheck";
import type { ClickToPickApiBean } from "../../pojo/FormI";
import type { Item } from "../../pojo/Item";
import type { Proto } from "../../pojo/Proto";
import type { S25ItemI } from "../../pojo/S25ItemI";
import type { DefinitelyArray } from "../../pojo/Util";
import type { EventResourcePref } from "../../services/event.service";
import { S25Date } from "../../util/core/s25.date";
import type { EventFormObjSearch } from "./object-availability/event.form.object.availability.component";

/**
 * The master type for the angJS-created EventForm bean
 * TODO: break this up into manageable, change-detectable pieces in EventFormStore
 */
export interface EventForm {
    eventId: number;
    profileId: number;
    eventName?: string;
    /** Earliest and latest dates the click-to-pick calendar will offer. */
    maxCreateDate: Date;
    minCreateDate: Date;
    copyEventId?: string;
    mode?: string;
    profileCode?: string;
    /** How a pattern replace lines old occurrences up with new ones. */
    mapping?: string;
    /** Held while a batch of new occurrences is built, so each one does not request its own reservation id. */
    deferRsrvIdGeneration?: boolean;
    occurrences: EventForm.Occurrence[];
    /** Set by hydration when the profile has binding reservations; location add/remove confirms when true. */
    hasBinding?: boolean;
    /** Date occurrences table: whether location or resource search is offered on this form. */
    hasSearch?: boolean;
    hasLocationSearch?: boolean;
    hasResourceSearch?: boolean;
    /** Form-level flag from the date/time picker. An occurrence has its own `spansMidnight`. */
    spansMidnight?: boolean;
    boundOccurrences?: EventForm.BoundOccurrence[];
    locations?: EventForm.ObjectItem[];
    resources?: EventForm.ObjectItem[];
    overrideRmAp?: boolean;
    overrideRsAp?: boolean;
    itemDict?: {
        expected?: { data?: number | string };
        registered?: { data?: number | string };
        space?: EventForm.ObjectFieldModel;
        resource?: EventForm.ObjectFieldModel;
        contacts?: { data?: { role_id: number; data?: { itemId?: number } }[] };
    };
    suppressedDatetimeFields?: string[];
    /**
     * The datetime picker sets this once it has built the occurrences, which the
     * details list needs to work with.
     */
    initialDatetimePickerInit?: boolean;
    hasPrivateLocations?: boolean;
    hasPrivateResources?: boolean;
    canSearchLocations?: boolean;
    canSearchResources?: boolean;
    hasRecommendedLocation?: boolean;
    hasRecommendedResource?: boolean;
    excludeExpressLocations?: boolean;
    recommendedLocations?: { itemId: number }[];
    recommendedResources?: { itemId: number }[];
    spacePrefSearch?: EventForm.ProfilePrefSearch;
    resourcePrefSearch?: EventForm.ProfilePrefSearch;
    /**
     * Pending location/resource preferences from event_text.
     * Used so Associated Resources search can show requested quantities (ANG-7556).
     */
    prefRsrvList?: EventForm.PrefReservation[];
    /** Saved associated resource preferences from profile.resource_pref. */
    resourcePrefs?: EventForm.PrefResource[];
    dirtySearch?: Record<EventForm.ObjectTypeId, boolean>;
    searchCounts?: Partial<Record<EventForm.ObjectTypeId, Proto.NumericalBoolean>>;
    checkHideConflicts?: boolean;
    hideHideConflicts?: boolean;
    checkHideRequestConflicts?: boolean;
    hideHideRequestConflicts?: boolean;
    checkEnforceHeadcount?: boolean;
    hideEnforceHeadcount?: boolean;
    skipLocationInitialAvailCheck?: boolean;
    availabilityPromises?: PromiseLike<unknown>[];
    availabilityChecksInFlight?: number;
    /** Truthy while the occurrences modal is open so the form defers its details-list rebuild. */
    modal?: unknown;
    needsDetailsListRecreate?: boolean;
    actionModel?: Partial<Record<EventForm.ObjectTypeId, { listModelBean?: EventForm.ObjectSearchListModelBean }>>;
    /** Shared with the date/time picker; `minutes` drives occurrence setup/takedown when synced. */
    timeModel: {
        evStartDt?: Date;
        evEndDt?: Date;
        minutes: EventForm.Occurrence.Minutes;
    };
    /** Registered by the click-to-pick calendar; the date occurrences table refreshes it after removals. */
    clickToPickApiBean?: ClickToPickApiBean;
    /** Returns the new occurrence, or nothing when `ifNotExists` is set and that date already has one. */
    addOccurrence?(
        evStartDt: Date,
        evEndDt: Date,
        minutes: EventForm.Occurrence.Minutes,
        ifNotExists: boolean,
        profileCode: string,
        locations: EventForm.ObjectItem[],
        resources: EventForm.ObjectItem[],
    ): EventForm.Occurrence | undefined;
    removeOccurrence?(occ: EventForm.Occurrence, profileCodeOverride?: string): void;
    updateProfileCode?(profileCode: string): void;
    assignMissingRsrvIds?(): Promise<void> | void;
    refreshAvailability?(
        specificOccs: EventForm.Occurrence[] | null,
        autoRecreateDetailsList?: boolean,
        itemTypeId?: EventForm.ObjectTypeId,
        noDirt?: boolean,
    ): Promise<unknown>;
    /** The availability search adds its picked items through this. */
    addObject(
        item: EventFormObjSearch.AngJSObject,
        skipPending?: boolean,
        action?: string,
        skipFromAddObjectFlag?: boolean,
        fromPrefSearch?: boolean,
    ): Promise<unknown> | unknown;

    /** Every `ObjectOccurrence`, keyed by `objOccHash`. `getObjOcc` reads and writes it; `updateObjOcc` fills it. */
    objectOccMap?: Record<string, EventForm.ObjectOccurrence>;
    objOccHash(itemTypeId: EventForm.ObjectTypeId, itemId: number, occUUID: number): string;
    /** update {@link objectOccMap} with every object-occurrence pair */
    updateObjOcc(): void;
    getObjOcc(item: EventForm.ObjectItem, occ: EventForm.Occurrence, noPut: boolean): EventForm.ObjectOccurrence;
    getObjOccs?: (includeLoc?: boolean, includeRes?: boolean) => EventForm.ObjectOccurrence[];

    getLocOccs(): EventForm.ObjectOccurrence[];
    getResOccs(): EventForm.ObjectOccurrence[];
    setSearchesDirty?: (itemTypeId?: number) => void;
    refreshOccAvailability?: (occ?: EventForm.Occurrence, autoRecreateDetailsList?: boolean) => Promise<unknown> | void;
    removeObject(item: EventForm.ObjectItem): void | Promise<unknown>;
    setSchedulerFromLocation?(item: EventForm.ObjectItem | null): void;
    recreateDetailsList(): unknown;
    checkQuotas?(formModel: EventForm): unknown;
    syncItemsCheckedObjWithListFn?(items: EventForm.ObjectItem[]): () => void;
    refreshObjOccAvailability?(item: EventForm.ObjectItem, occs: EventForm.Occurrence[]): Promise<unknown>;
    refreshObjOccPending?(item: EventForm.ObjectItem, occs: EventForm.Occurrence[]): Promise<boolean>;
    $broadcast?(eventName: EventForm.BroadcastEvent, data?: unknown): void;
    undoLayoutAdditionalTime?(layout: Layout): void;
    updateSetupTakedownFromLayout?(layout: Layout): void;
    runRules?(): void;
}

export namespace EventForm {
    export const enum BroadcastEvent {
        /** Matches AngularJS `EventFormObjectDetailsConst.refreshOccBasedData`. */
        ObjectDetailsRefreshOccBasedData = "s25-event-form-detail-refresh-occ-based-data",
    }

    /** Availability checks build full `ConflictDefaults`; event hydration only fills in what it knows. */
    export type ObjectOccurrenceConflict = Partial<AvailCheck.ConflictDefaults>;

    export interface ObjectOccurrenceExceedingQuantity {
        start: Date;
        end: Date;
        occIds: Set<number>;
        reserved: number;
        max: number;
        occs?: ObjectOccurrence[];
    }

    export interface ObjectOccurrenceCheckedObj {
        conflicts?: ObjectOccurrenceConflict[];
        exceedingQuantity?: ObjectOccurrenceExceedingQuantity[];
    }

    export type ObjectTypeId = Item.Ids.Location | Item.Ids.Resource;

    /** A location or resource on the form model, once `setLocationDetails` / `setResourceDetails` has run. */
    export interface ObjectItem {
        itemId: number;
        itemTypeId: ObjectTypeId;
        itemName?: string;
        formal_name?: string;
        layouts?: Layout[];
        max_capacity?: number | string;
        stock_level?: number;
        always_shared?: Proto.CharBoolean;
        defaultInstructions?: string;
        quantity?: number;
        assign_buffer_minutes?: number;
        assign_buffer_string?: string;
        assign_buffer_perm?: string;
        apRestriction?: AvailCheck.CheckedItem["apRestriction"];
        /** Occurrence uuids currently blocked by assignment policy; the occurrence rows read this. */
        apWarningUUIDs?: Set<number>;
        scheduler?: { itemId?: number };
        /** Resource only: the quantity the user asked for when adding it from the search. */
        defaultResourceQuantity?: number;
        checkedObj?: ObjectOccurrenceCheckedObj;
    }

    export interface Occurrence {
        /** Counter on the form model (`model.occUUID++`), matching `ConflictDefaults.occUUID`. */
        uuid: number;
        /** Reservation id once the occurrence has been saved; absent on occurrences added this session. */
        rsrvId?: number;
        evStartDt: Date;
        evEndDt: Date;
        comment?: string;
        /** Reservation state. The date occurrences table edits this; availability skips cancelled and confirmed. */
        state: S25ItemI;
        minutes?: Occurrence.Minutes;
        spansMidnight?: boolean;
        /** Unchecked in the date occurrences table to leave this occurrence out of availability searches. */
        includeInSearch?: boolean;
        prevStartTime?: string;
        prevEndTime?: string;
        /** Locations and resources assigned on this occurrence, as the date occurrences table shows them. */
        locations?: S25ItemI[];
        resources?: S25ItemI[];
    }

    export namespace Occurrence {
        /** Buffer minutes around the event time; setup/pre widen the start, post/takedown widen the end. */
        export interface Minutes {
            setupMinutes?: number;
            preMinutes?: number;
            postMinutes?: number;
            takedownMinutes?: number;
        }
    }

    export interface ObjectOccurrence {
        occ: Occurrence;
        item: ObjectItem;
        /** Set once a rule has applied its quantity, so the user's later edits stick. */
        preventRuleQuantity?: boolean;
        isIncluded?: boolean;
        spaceShare?: boolean;
        layout?: Layout;
        layoutImageId?: number | null;
        showLayoutPreview?: boolean;
        spaceInstructions?: string;
        resourceInstructions?: string;
        spaceAttendance?: number | string;
        resourceQuantity?: number | string;
        prevResourceQuantity?: number | string;
        maxQuantity?: number;
        maxQuantityDisplay?: string | number;
        startLabel?: string;
        endLabel?: string;
    }

    export namespace ObjectOccurrence {
        /** Advance notice (`assign_buffer`) blocks the request when the start falls inside the buffer. */
        export function needsAdvanceNotice(objOcc: ObjectOccurrence, currentDateTime: Date): boolean {
            if (!objOcc.item.assign_buffer_minutes || objOcc.item.assign_buffer_perm !== "noRequest") return false;
            const bufferDateTime = S25Date.addMinutes(currentDateTime, objOcc.item.assign_buffer_minutes);
            return EventFormOccurrenceUtil.getStartDt(objOcc.occ) < bufferDateTime;
        }

        /** Assignment policy blocks requesting this occurrence at its current time. */
        export function blockedByAssignmentPolicy(objOcc: ObjectOccurrence): boolean {
            return !!(
                objOcc.item.apRestriction?.apNoRequest && objOcc.item.apRestriction?.occUUIDs?.has(objOcc.occ.uuid)
            );
        }
    }

    /** Fields the occurrences table can edit; used to cancel in-progress modal input. */
    export type ObjectOccurrenceEdits = Pick<
        ObjectOccurrence,
        | "isIncluded"
        | "spaceShare"
        | "layout"
        | "layoutImageId"
        | "showLayoutPreview"
        | "spaceInstructions"
        | "resourceInstructions"
        | "spaceAttendance"
        | "resourceQuantity"
        | "prevResourceQuantity"
    >;

    export type ObjectFieldValidationError = { message: string; target: string };

    /** The form field ("space" or "resource") the object item is bound to. */
    export interface ObjectFieldModel {
        uuid: string;
        id?: string;
        forceHidden?: boolean;
        action?(): void;
        validate?(): ObjectFieldValidationError[] | boolean;
    }

    /** An occurrence on another event this one is bound to; searched alongside the open event's dates. */
    export interface BoundOccurrence {
        eventId: number;
        eventName?: string;
        profileId: number;
        reservationId: number;
        reservationState: number;
        startDt: Date;
        endDt: Date;
    }

    export interface ProfilePrefSearch {
        txt: string;
        val: string;
        grp?: string;
        type?: string;
        isAssociatedLocations?: boolean;
    }

    /** Preference / pending-approval resource row from event_text (text_type_id 9). */
    export interface PrefResourceReservation {
        resource_id: number | string;
        quantity?: number | string;
        resource_name?: string;
    }

    export interface PrefReservation {
        reservation_id?: number | string;
        resource_reservation?: PrefResourceReservation[];
    }

    /** Saved associated resource from profile.resource_pref.pref_resource. */
    export type PrefResource = DefinitelyArray<EventResourcePref["pref_resource"]>[number];

    /** Row handles the AngularJS form reads back off the availability search's results list. */
    export interface ObjectSearchListModelBean {
        showColumn: () => void;
        hideColumn: () => void;
        getCell?: (rowIndex: number, colPrefname: string) => unknown;
        getRow?: (itemId: number) => number | undefined;
        deferred: undefined;
        scope: undefined;
    }
}
